import { useListContext } from "ra-core";
import type { ReactElement } from "react";
import { useEffect } from "react";

/** Custom field filters come from the settings, which may load later. */
const isCustomFieldFilter = (source: string) =>
  source.startsWith("custom_fields");

/**
 * Drops the saved filters the list no longer offers, like those of the old
 * left column: hidden from the top bar, they would keep the list empty with
 * no way to remove them.
 */
export const KnownFiltersOnly = ({ filters }: { filters: ReactElement[] }) => {
  const { filterValues, displayedFilters, setFilters } = useListContext();
  const known = new Set(
    filters.map((filter) => (filter.props as { source: string }).source),
  );
  const isKnown = (source: string) =>
    known.has(source) || isCustomFieldFilter(source);
  const hasUnknown = Object.keys(filterValues ?? {}).some(
    (source) => !isKnown(source),
  );

  useEffect(() => {
    if (!hasUnknown) return;
    const keep = <T,>(values: Record<string, T> = {}) =>
      Object.fromEntries(
        Object.entries(values).filter(([source]) => isKnown(source)),
      );
    setFilters(keep(filterValues), keep(displayedFilters), false);
    // Runs once per unknown filter found; the cleaned filters end it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnknown]);

  return null;
};
